import { useEffect, useMemo, useState } from "react";
import { planApi } from "../../data/api/PlanApi";
import { questionnaireApi } from "../../data/api/QuestionnaireApi";
import { Plan } from "../../data/models/Plan/PlanModel";
import {
  Questionnaire,
  QuestionnaireAssignPlansPayload,
  QuestionnaireCreatePayload,
  QuestionnaireUpdatePayload,
  QuestionnaireVersion,
  QuestionnaireVersionCreatePayload,
} from "../../data/models/Questionnaire/QuestionnaireModel";

const sortVersions = (versions: QuestionnaireVersion[] = []) =>
  [...versions].sort((a, b) => b.versionNumber - a.versionNumber);

const normalizeQuestionnaire = (questionnaire: Questionnaire): Questionnaire => {
  const versions = sortVersions(questionnaire.versions ?? []);
  const activeVersion =
    versions.find((version) => version.status === "PUBLISHED") ?? null;

  return {
    ...questionnaire,
    versions,
    activeVersion,
  };
};

const normalizePlanCodes = (planCodes: string[] = []) =>
  Array.from(new Set(planCodes.map((code) => code.trim()).filter(Boolean))).sort();

export function useAdminQuestionnairesPage() {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    string | null
  >(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [questionnairesRes, plansRes] = await Promise.all([
        questionnaireApi.adminList.request(null),
        planApi.allPlans.request(null),
      ]);

      setQuestionnaires(
        questionnairesRes.isSuccess && Array.isArray(questionnairesRes.data)
          ? questionnairesRes.data.map(normalizeQuestionnaire)
          : [],
      );
      setPlans(
        plansRes.isSuccess && Array.isArray(plansRes.data) ? plansRes.data : [],
      );
    } finally {
      setLoading(false);
    }
  };

  const upsertQuestionnaire = (questionnaire: Questionnaire) => {
    const normalized = normalizeQuestionnaire(questionnaire);

    setQuestionnaires((prev) => {
      const index = prev.findIndex((item) => item.id === normalized.id);
      if (index === -1) {
        return [normalized, ...prev];
      }

      const next = [...prev];
      next[index] = normalized;
      return next;
    });

    return normalized;
  };

  const mergeQuestionnaireVersion = (
    questionnaireId: string,
    version: QuestionnaireVersion,
    options?: {
      archivePublished?: boolean;
      questionnaireStatus?: Questionnaire["status"];
    },
  ) => {
    setQuestionnaires((prev) =>
      prev.map((questionnaire) => {
        if (questionnaire.id !== questionnaireId) {
          return questionnaire;
        }

        const existingVersions = [...questionnaire.versions];
        const nextVersions = existingVersions.some((item) => item.id === version.id)
          ? existingVersions.map((item) =>
              item.id === version.id ? { ...item, ...version } : item,
            )
          : [...existingVersions, version];

        const normalizedVersions = sortVersions(
          nextVersions.map((item) => {
            if (
              options?.archivePublished &&
              item.id !== version.id &&
              item.status === "PUBLISHED"
            ) {
              return {
                ...item,
                status: "ARCHIVED",
                isActive: false,
              };
            }

            return item;
          }),
        );

        const activeVersion =
          normalizedVersions.find((item) => item.status === "PUBLISHED") ?? null;

        return {
          ...questionnaire,
          ...(options?.questionnaireStatus
            ? { status: options.questionnaireStatus }
            : {}),
          versions: normalizedVersions,
          activeVersion,
        };
      }),
    );
  };

  useEffect(() => {
    void fetchAll();
  }, []);

  const selectedQuestionnaire = useMemo(
    () =>
      questionnaires.find(
        (questionnaire) => questionnaire.id === selectedQuestionnaireId,
      ) ?? null,
    [questionnaires, selectedQuestionnaireId],
  );

  const selectedVersion = useMemo(() => {
    if (!selectedQuestionnaire) return null;
    return (
      selectedQuestionnaire.versions.find(
        (version) => version.id === selectedVersionId,
      ) ?? selectedQuestionnaire.activeVersion ?? selectedQuestionnaire.versions[0] ?? null
    );
  }, [selectedQuestionnaire, selectedVersionId]);

  const setSelectedQuestionnaire = (questionnaireId: string | null) => {
    setSelectedQuestionnaireId(questionnaireId);
    const questionnaire = questionnaires.find(
      (item) => item.id === questionnaireId,
    );
    setSelectedVersionId(
      questionnaire?.activeVersion?.id ??
        questionnaire?.versions?.[0]?.id ??
        null,
    );
  };

  const setVersion = (versionId: string | null) => {
    setSelectedVersionId(versionId);
  };

  const createQuestionnaire = async (
    payload: QuestionnaireCreatePayload,
  ): Promise<Questionnaire | null> => {
    setSaving(true);
    try {
      const res = await questionnaireApi.adminCreate.request(payload);
      if (res.isSuccess) {
        const created = res.data;
        if (created) {
          upsertQuestionnaire(created);
          setSelectedQuestionnaireId(created.id);
          setSelectedVersionId(
            created.activeVersion?.id ?? created.versions?.[0]?.id ?? null,
          );
        }
        return created ?? null;
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const updateQuestionnaire = async (payload: QuestionnaireUpdatePayload) => {
    setSaving(true);
    try {
      const res = await questionnaireApi.adminUpdate.request(payload);
      if (res.isSuccess && res.data) {
        upsertQuestionnaire(res.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const createVersion = async (
    payload: QuestionnaireVersionCreatePayload,
  ): Promise<QuestionnaireVersion | null> => {
    setSaving(true);
    try {
      const res = await questionnaireApi.adminCreateVersion.request(payload);
      if (res.isSuccess) {
        if (res.data) {
          mergeQuestionnaireVersion(String(payload.questionnaireId), res.data);
          setSelectedVersionId(res.data.id);
          return res.data;
        }
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publishVersion = async (
    versionId: number,
  ): Promise<QuestionnaireVersion | null> => {
    setSaving(true);
    try {
      const res = await questionnaireApi.adminPublishVersion.request({ versionId });
      if (res.isSuccess && res.data) {
        mergeQuestionnaireVersion(String(res.data.questionnaireId), res.data, {
          archivePublished: true,
          questionnaireStatus: "ACTIVE",
        });
        setSelectedVersionId(res.data.id);
        return res.data;
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const assignPlans = async (payload: QuestionnaireAssignPlansPayload) => {
    const currentPlanCodes = normalizePlanCodes(
      questionnaires.find((item) => item.id === String(payload.questionnaireId))
        ?.planAssignments.map((assignment) => assignment.planCode) ?? [],
    );
    const nextPlanCodes = normalizePlanCodes(payload.planCodes);
    const shouldPersistPlans =
      currentPlanCodes.length !== nextPlanCodes.length ||
      currentPlanCodes.some((code, index) => code !== nextPlanCodes[index]);

    if (!shouldPersistPlans) {
      return;
    }

    setSaving(true);
    try {
      const res = await questionnaireApi.adminAssignPlans.request(payload);
      if (res.isSuccess && res.data) {
        upsertQuestionnaire(res.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveQuestionnaireDetails = async (
    payload: QuestionnaireUpdatePayload & { planCodes: string[] },
  ): Promise<Questionnaire | null> => {
    setSaving(true);
    try {
      const currentPlanCodes = normalizePlanCodes(
        questionnaires.find((item) => item.id === String(payload.questionnaireId))
          ?.planAssignments.map((assignment) => assignment.planCode) ?? [],
      );
      const nextPlanCodes = normalizePlanCodes(payload.planCodes);
      const shouldPersistPlans =
        currentPlanCodes.length !== nextPlanCodes.length ||
        currentPlanCodes.some((code, index) => code !== nextPlanCodes[index]);

      const updateRes = await questionnaireApi.adminUpdate.request({
        questionnaireId: payload.questionnaireId,
        code: payload.code,
        name: payload.name,
        description: payload.description,
        status: payload.status,
      });

      if (!updateRes.isSuccess || !updateRes.data) {
        return null;
      }

      let questionnaire = upsertQuestionnaire(updateRes.data);

      if (shouldPersistPlans) {
        const assignRes = await questionnaireApi.adminAssignPlans.request({
          questionnaireId: payload.questionnaireId,
          planCodes: payload.planCodes,
        });

        if (assignRes.isSuccess && assignRes.data) {
          questionnaire = upsertQuestionnaire(assignRes.data);
        }
      }

      return questionnaire;
    } finally {
      setSaving(false);
    }
  };

  const applySavedVersionSchema = (
    questionnaireId: string,
    versionId: string,
    schema: any,
  ) => {
    setQuestionnaires((prev) =>
      prev.map((questionnaire) => {
        if (questionnaire.id !== questionnaireId) {
          return questionnaire;
        }

        const versions = questionnaire.versions.map((version) =>
          version.id === versionId
            ? {
                ...version,
                schema,
                updatedAt: new Date().toISOString(),
              }
            : version,
        );

        return {
          ...questionnaire,
          versions: sortVersions(versions),
          activeVersion:
            versions.find((item) => item.status === "PUBLISHED") ?? null,
        };
      }),
    );
  };

  return {
    questionnaires,
    plans,
    loading,
    saving,
    selectedQuestionnaireId,
    selectedVersionId,
    selectedQuestionnaire,
    selectedVersion,
    setSelectedQuestionnaire,
    setVersion,
    createQuestionnaire,
    updateQuestionnaire,
    createVersion,
    publishVersion,
    assignPlans,
    saveQuestionnaireDetails,
    applySavedVersionSchema,
    refresh: fetchAll,
  };
}
